import {type ChangeEvent, type DragEvent, useState} from 'react';

import {useThemeColors} from '@nfq/colors';

/**
 * Custom hook `useUploadInput` containing all the logic for the `UploadInput` component.
 * This hook manages file upload functionality including file selection, deletion, and drag-and-drop operations.
 * It provides state management for the file list and handles all user interactions with the upload input.
 *
 * @returns An object containing theme colors, file list state, and event handlers for upload operations.
 *
 * @example
 * ```tsx
 * const {
 *     colors,
 *     fileList,
 *     handleChange,
 *     handleDragOver,
 *     handleFileDelete
 * } = useUploadInput();
 * ```
 */
export const useUploadInput = () => {
    const colors = useThemeColors();
    const [fileList, setFileList] = useState<File[]>([]);

    /**
     * Handles file selection and initiates the upload process.
     * This function reads the selected files, checks for file size constraints,
     * and uploads each file while tracking progress.
     *
     * @param event The change event from the file input element.
     *
     * @example
     * ```tsx
     * <input type="file" onChange={handleChange} />
     * ```
     */
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const {files: filesOrig} = event.target;

        if (filesOrig) {
            const files = Array.from(filesOrig);

            setFileList(prev => {
                const set = new Set([...prev, ...files]);

                return Array.from(set);
            });
        }
    };

    /**
     * Deletes a file from the upload list.
     * This function resets the progress and error states for the deleted file
     * and removes it from the `fileList` array.
     *
     * @param fileIndex The index of the file to be removed from the list.
     *
     * @example
     * ```tsx
     * <button onClick={() => handleFileDelete(0)}>Delete first file</button>
     * ```
     */
    const handleFileDelete = (fileIndex: number) => {
        setFileList(prev => prev.toSpliced(fileIndex, 1));
    };

    /**
     * Handles the drag-over event for file inputs.
     * Prevents default behavior and sets the appropriate drop effect.
     *
     * @param event The drag event from the file input element.
     *
     * @example
     * ```tsx
     * <div onDragOver={handleDragOver}>Drop files here</div>
     * ```
     */
    const handleDragOver = (event: DragEvent<HTMLInputElement>): void => {
        event.stopPropagation();
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.dataTransfer.dropEffect = 'copy';
    };

    return {
        colors,
        fileList,
        handleChange,
        handleDragOver,
        handleFileDelete
    };
};